import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from "../../components/ui/input";
import Checkbox from "antd/es/checkbox/Checkbox";
import { Save, ChevronDown, ChevronUp, ChevronLeftIcon, UserCircle } from 'lucide-react';
import { cn } from '../../utils/tailwindUtils';
import useApiMethods from '../../lib/useApiMethods';
import { USERPERMISSION } from '../../constants/api';
import ToastMsg from '../../components/common/ToastMsg';
import { ContainerCard } from '../../components/ui/cards';
import { isOvertimeRequestWorkflowEnabled } from '../../utils/customConfigSession';
import {
    applyEssentialsToPermissions,
    getActiveEmployeeEssentials,
    getEssentialsGrantState,
    isEssentialPermission,
} from '../../config/employeeBasicsPermissions';
import { PERMISSION_CHIP } from '../../config/antdTheme';

const UserPermissionEdit = () => {
    const { fetch, update } = useApiMethods();
    const navigate = useNavigate();
    const location = useLocation();
    const { state } = location;

    const [loading, setLoading] = useState(false);
    const [expandedSections, setExpandedSections] = useState(new Set());
    const [formData, setFormData] = useState({
        id: null,
        userGroup_name: "",
        userGroup_id: null,
        notes: "",
        permissions: []
    });

    const customConfig = JSON.parse(sessionStorage.getItem("customConfig") || "{}");
    const isNabhEnabled = !!customConfig.is_nabh;
    const isOvertimeEnabled = isOvertimeRequestWorkflowEnabled();

    const employeeEssentials = useMemo(
        () => getActiveEmployeeEssentials({
            nabhEnabled: isNabhEnabled,
            overtimeWorkflowEnabled: isOvertimeEnabled,
        }),
        [isNabhEnabled, isOvertimeEnabled]
    );

    const isAdministratorGroup = ['Administrator', 'Administartor'].includes(
        (formData.userGroup_name || '').trim()
    );

    const essentialsState = useMemo(
        () => getEssentialsGrantState(formData.permissions, employeeEssentials),
        [formData.permissions, employeeEssentials]
    );

    // Permission structure that matches your API response
    const permissionStructure = [
        { key: 'home', label: 'Home', type: 'single' },
        {
            key: 'administrator',
            label: 'Admin',
            type: 'group',
            subItems: [
                { key: 'organization', label: 'Organization' },
                { key: 'user_group', label: 'User Group' },
                { key: 'user_permission', label: 'User Permission' },
                { key: 'notice_board', label: 'Notice Board' },
                { key: 'password_policy', label: 'Password Policy' },
                { key: 'organization_documents', label: 'Organization Documents' }
            ]
        },
        {
            key: 'master',
            label: 'Master',
            type: 'group',
            subItems: [
                { key: 'employee_type', label: 'Employee Type' },
                { key: 'department', label: 'Department' },
                { key: 'designation', label: 'Designation' },
                { key: 'city', label: 'City' },
                { key: 'block', label: 'Block' },
                { key: 'floor', label: 'Floor' },
                { key: 'room', label: 'Room' },
                { key: 'leave_type', label: 'Leave Type' },
                { key: 'employee_group', label: 'Employee Group' },
                { key: 'reimbursements_type', label: 'Reimbursements Type' },
                { key: 'asset_type', label: 'Asset Type' },
                { key: 'asset_category', label: 'Asset Category' },
                { key: 'asset_sub_category', label: 'Asset Sub Category' },
                { key: 'letter_type', label: 'Letter Type' },
                { key: 'letter_template', label: 'Letter Template' },
                { key: 'document_type', label: 'Document Type' }
            ]
        },
        {
            key: 'configuration',
            label: 'Configuration',
            type: 'group',
            subItems: [
                { key: 'shift_configuration', label: 'Shift Configuration' },
                { key: 'holiday', label: 'Holiday' },
                { key: 'week_off', label: 'Week off' },
                { key: 'payroll_period', label: 'Payroll Period' },
                { key: 'subordinate', label: 'Subordinate' },
                { key: 'custom_configuration', label: 'Custom Configuration' },
                { key: 'salary_component', label: 'Salary Component' },
                { key: 'salary_template', label: 'Salary Template' },
                { key: 'da_revision', label: 'DA Revision' },
            ]
        },
        {
            key: 'employee',
            label: 'Employee',
            type: 'group',
            subItems: [
                { key: 'employees', label: 'Employee List' },
                { key: 'employee_remarks', label: 'Employee Remarks' },
                { key: 'employee_allowances', label: 'Employee Allowances' },
                { key: 'employee_deductions', label: 'Employee Deductions' },
                // This list is hardcoded, so a permission column added to
                // EmployeeModule does NOT show up here until it is added by hand.
                // A screen is only grantable once its key appears in this list —
                // worth knowing when adding the next one.
                { key: 'employee_deposits', label: 'Employee Deposits' },
                { key: 'leave_request', label: 'Leave Request' },
                { key: 'reimbursements_request', label: 'Reimbursement Request' },
                { key: 'advance_salary_request', label: 'Advance Salary Request' },
                { key: 'overtime_request', label: 'Overtime Request' },
                { key: 'absconding', label: 'Abscond' },
                { key: 'approval_inbox', label: 'Approval Inbox' },
                { key: 'team_timesheet', label: 'Team Timesheet' },
                { key: 'employee_onboarding', label: 'Employee Onboarding' },
                { key: 'intern_onboarding', label: 'Intern Onboarding' },
            ]
        },
        {
            key: 'payroll',
            label: 'Payroll',
            type: 'group',
            subItems: [
                { key: 'run_payroll', label: 'Run Payroll' },
                { key: 'payroll', label: 'Payroll' },
                { key: 'payslips', label: 'Payslips' },
                // TDS Preview shows every employee's salary and tax projection;
                // TDS Configuration sets the statutory rates for the company.
                // Separate keys because they suit very different audiences.
                { key: 'tds_preview', label: 'TDS Preview' },
                { key: 'tds_configuration', label: 'TDS Configuration' },
                { key: 'payroll_hold', label: 'Payroll Hold' },
                { key: 'attendance_penalties', label: 'Attendance Penalties' },
                { key: 'perk_criteria', label: 'Perk Criteria' }
            ]
        },
        {
            key: 'attendance',
            label: 'Attendance',
            type: 'group',
            subItems: [
                { key: 'day_wise_attendance', label: 'Day Attendance' },
                { key: 'attendance_log', label: 'Attendance Log' },
                { key: 'punch_discrepancies', label: 'Punch Discrepancies' },
                { key: 'penalty_appeals', label: 'Penalty Appeals' },
                { key: 'shift_change_requests', label: 'Shift Change Requests' },
                { key: 'timesheet', label: 'My Timesheet' },
            ]
        },
        {
            key: 'asset_management',
            label: 'Asset Management',
            type: 'group',
            subItems: [
                { key: 'asset_request', label: 'Asset Request' },
                { key: 'asset_registry', label: 'Asset Registry' },
                { key: 'asset_employee_audit', label: 'Asset Employee Audit' }
            ]
        },
        {
            key: 'reports',
            label: 'Reports',
            type: 'group',
            subItems: [
                { key: 'day_wise_attendance', label: 'Day wise Attendance' },
                { key: 'month_wise_attendance', label: 'Month Wise Attendance' },
                { key: 'employee_esi_statement', label: 'Employee ESI Statement' },
                { key: 'employee_pf_statement', label: 'Employee PF Statement' },
                { key: 'salary_process_bank', label: 'Salary Process Bank' },
                { key: 'payroll_detailed_summary', label: 'Payroll Detailed Summary' },
                { key: 'employee_earning_deduction', label: 'Employee Earning Deduction' },
                { key: 'muster_roll', label: 'Muster Roll' },
                { key: 'subordinates', label: 'Subordinates' },
                { key: 'employee_salary_summary', label: 'Employee Salary Summary' },
                { key: 'attendance_correction', label: 'Attendance Correction' },
                { key: 'roster_report', label: 'Roster Report' },
                { key: 'leave_permission_report', label: 'Leave Permission Report' },
                { key: 'short_duty_report', label: 'Short-Duty Review' },
                { key: 'fine_report', label: 'Fine Report' },
                { key: 'document_upload_report', label: 'Document Upload Report' }
            ]
        },
        ...(isNabhEnabled ? [{
            key: 'training',
            label: 'Training',
            type: 'group',
            subItems: [
                { key: 'my_trainings', label: 'My Trainings' },
                { key: 'team_trainings', label: 'Team Trainings' },
                { key: 'assign_training', label: 'Assign Training' }
            ]
        }] : []),
        {
            key: 'appraisal',
            label: 'Appraisal',
            type: 'group',
            subItems: [
                { key: 'my_appraisals', label: 'My Appraisals' },
                { key: 'team_appraisals', label: 'Team Appraisals' },
                { key: 'appraisal_kpis', label: 'KPI Library' },
                { key: 'appraisal_cycles', label: 'Appraisal Cycles' },
                { key: 'appraisal_approval', label: 'Final Approval' },
            ]
        },
        {
            key: 'grievance',
            label: 'Grievance',
            type: 'group',
            subItems: [
                { key: 'my_grievances', label: 'My Grievances' },
                { key: 'grievance_inbox', label: 'Grievance Inbox' },
                { key: 'grievance_categories', label: 'Grievance Categories' },
                { key: 'grievance_dashboard', label: 'Grievance Dashboard' },
            ]
        },
        { key: 'roster', label: 'Roster', type: 'single' }
    ];

    // Initialize permissions
    const normalizePermissionFlags = (flags) => {
        if (!flags) return flags;
        return {
            permission: !!flags.permission,
            active: !!flags.active,
            edit: !!flags.edit,
            add: !!flags.add,
            draft: !!flags.draft,
        };
    };

    const initializePermissions = (apiPermissions = []) => {
        const defaultPermissions = permissionStructure.map(section => {
            if (section.type === 'single') {
                const apiModule = apiPermissions.find(p => p.module_name === section.key);
                const rawFlags = apiModule?.module_list?.[section.key] || {
                    permission: false,
                    active: false,
                    edit: false,
                    add: false,
                    draft: false
                };
                return {
                    module_name: section.key,
                    module_list: {
                        [section.key]: normalizePermissionFlags(rawFlags)
                    }
                };
            } else {
                const apiModule = apiPermissions.find(p => p.module_name === section.key);
                const moduleList = {};

                section.subItems.forEach(subItem => {
                    const rawFlags = apiModule?.module_list?.[subItem.key] || {
                        permission: false,
                        active: false,
                        edit: false,
                        add: false,
                        draft: false
                    };
                    moduleList[subItem.key] = normalizePermissionFlags(rawFlags);
                });

                return {
                    module_name: section.key,
                    module_list: moduleList
                };
            }
        });

        return defaultPermissions;
    };

    const getPermissionData = useCallback(async () => {
        try {
            setLoading(true);
            const res = await fetch(USERPERMISSION, { id: state?.usergroup_id });

            if (res?.data?.result?.[0]) {
                const data = res.data.result[0];
                const initializedPermissions = initializePermissions(data.permissions);

                setFormData({
                    id: data.id,
                    userGroup_name: data.usergroup?.user_group_name || "",
                    userGroup_id: data.usergroup?.usergroup_id || null,
                    notes: data.notes || "",
                    permissions: initializedPermissions
                });
            }
        } catch (error) {
            console.error('Error fetching permission data:', error);
            ToastMsg('error', 'Failed to load permission data');
        } finally {
            setLoading(false);
        }
    }, [state?.usergroup_id]);

    useEffect(() => {
        if (state?.usergroup_id) {
            getPermissionData();
        } else {
            setFormData(prev => ({
                ...prev,
                permissions: initializePermissions()
            }));
        }
    }, [state?.usergroup_id, getPermissionData]);

    const getPermissionValue = (moduleName, permissionType, subItemKey = null) => {
        const module = formData.permissions.find(p => p.module_name === moduleName);
        if (!module) return false;

        if (subItemKey) {
            return module.module_list[subItemKey]?.[permissionType] || false;
        }

        const singleItem = module.module_list[moduleName];
        return singleItem?.[permissionType] || false;
    };

    const PERM_TYPES = ['permission', 'active', 'edit', 'add', 'draft'];

    /** True when a module group has mixed grants across its sub-items. */
    const isModulePartiallyGranted = (section) => {
        if (section.type !== 'group' || !section.subItems?.length) return false;

        const hasAnyGrant = section.subItems.some((sub) =>
            PERM_TYPES.some((permType) => getPermissionValue(section.key, permType, sub.key))
        );
        if (!hasAnyGrant) return false;

        return PERM_TYPES.some((permType) => {
            const checkedCount = section.subItems.filter(
                (sub) => getPermissionValue(section.key, permType, sub.key)
            ).length;
            return checkedCount > 0 && checkedCount < section.subItems.length;
        });
    };

    const getGroupColumnCheckState = (section, permType) => {
        const checkedCount = section.subItems.filter(
            (sub) => getPermissionValue(section.key, permType, sub.key)
        ).length;
        return {
            checked: checkedCount === section.subItems.length,
            indeterminate: checkedCount > 0 && checkedCount < section.subItems.length,
        };
    };

    const updatePermission = (moduleName, permissionType, value, subItemKey = null) => {
        setFormData(prev => {
            const newPermissions = prev.permissions.map(module => {
                if (module.module_name !== moduleName) return module;

                const updatedModule = { ...module };
                const targetKey = subItemKey || moduleName;
                const current = updatedModule.module_list[targetKey] || {
                    permission: false,
                    active: false,
                    edit: false,
                    add: false,
                    draft: false,
                };

                let next = { ...current, [permissionType]: value };

                updatedModule.module_list = {
                    ...updatedModule.module_list,
                    [targetKey]: next,
                };

                return updatedModule;
            });

            return { ...prev, permissions: newPermissions };
        });
    };

    const toggleSection = (key) => {
        setExpandedSections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(key)) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
            return newSet;
        });
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            const payload = {
                id: formData.id,
                notes: formData.notes,
                permissions: formData.permissions
            };

            const res = await update(USERPERMISSION, payload);

            if (res.status === "success") {
                ToastMsg("success", res.message);
                navigate('/admin/userpermission');
            } else {
                ToastMsg("error", res.message || "Failed to update permissions");
            }
        } catch (error) {
            console.error('Error updating permissions:', error);
            ToastMsg('error', 'Failed to update permissions');
        } finally {
            setLoading(false);
        }
    };

    const handleEssentialsToggle = (checked) => {
        setFormData((prev) => ({
            ...prev,
            permissions: applyEssentialsToPermissions(
                prev.permissions,
                employeeEssentials,
                checked
            ),
        }));
        ToastMsg(
            'success',
            checked
                ? `Employee essentials enabled (${employeeEssentials.length} modules)`
                : 'Employee essentials revoked — other permissions unchanged'
        );
    };

    const handleBack = () => {
        navigate('/admin/userpermission');
    };

    if (loading && !formData.id) {
        return (
            <div className="p-6 dark:bg-gray-900 min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sidebar mx-auto"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-400">Loading permissions...</p>
                </div>
            </div>
        );
    }

    return (
        <ContainerCard scrollable>
            {/* Header */}
            <div className="flex items-start gap-4 mb-4">
                <ChevronLeftIcon className="cursor-pointer shrink-0 mt-1" onClick={handleBack} />
                <div className="min-w-0 flex-1">
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                        Edit User Permissions
                    </h1>
                    {formData.userGroup_name && (
                        <div className="mt-3 inline-flex items-center gap-3 rounded-xl border-2 border-sidebar-primary bg-sidebar-primary px-4 py-2.5 shadow-sm">
                            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/20">
                                <UserCircle className="h-5 w-5 text-white" />
                            </span>
                            <div className="min-w-0">
                                <p className="text-[11px] font-semibold uppercase tracking-wider text-white/80">
                                    User Group
                                </p>
                                <p className="text-base font-bold text-white truncate">
                                    {formData.userGroup_name}
                                </p>
                            </div>
                        </div>
                    )}
                    <p className="text-sm text-muted-foreground mt-3">
                        Grant module access for this user group. Use employee essentials for common self-service screens, then fine-tune below.
                    </p>
                </div>
            </div>

            {/* Main Content */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border shadow-sm p-5">
                {!isAdministratorGroup && (
                    <div className="mb-6 rounded-lg border border-sidebar/20 bg-sidebar/5 dark:bg-sidebar/10 p-4">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                        Employee essentials
                                    </h2>
                                    {essentialsState === 'partial' && (
                                        <span className={PERMISSION_CHIP.partial}>
                                            Partially enabled
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Day attendance, attendance log, leave, reimbursement, advance salary, timesheet, and shift requests.
                                    You can override any module individually in the list below.
                                </p>
                                <ul className="mt-3 flex flex-wrap gap-1.5">
                                    {employeeEssentials.map((item) => (
                                        <li
                                            key={`${item.module}.${item.key}`}
                                            className="text-xs rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-gray-600 dark:text-gray-300"
                                        >
                                            {item.label}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="flex items-center gap-3 shrink-0 sm:pt-1">
                                <span className={cn(
                                    "text-sm font-medium",
                                    essentialsState === 'on'
                                        ? "text-sidebar-primary"
                                        : essentialsState === 'partial'
                                            ? "text-sidebar-primary/80"
                                            : "text-muted-foreground"
                                )}>
                                    {essentialsState === 'on' ? 'Enabled' : essentialsState === 'partial' ? 'Mixed' : 'Disabled'}
                                </span>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={essentialsState === 'on'}
                                    onClick={() => handleEssentialsToggle(essentialsState !== 'on')}
                                    disabled={loading}
                                    className={cn(
                                        "relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 shrink-0",
                                        essentialsState === 'on'
                                            ? "bg-sidebar-primary"
                                            : essentialsState === 'partial'
                                                ? "bg-sidebar-primary/45"
                                                : "bg-gray-300 dark:bg-gray-600",
                                        loading && "cursor-not-allowed opacity-50"
                                    )}
                                >
                                    <span
                                        className={cn(
                                            "inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 shadow-sm",
                                            essentialsState === 'on' ? "translate-x-6" : "translate-x-1"
                                        )}
                                    />
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="mb-8">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Notes
                    </label>
                    <Input
                        value={formData.notes}
                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Add notes about this permission profile..."
                        className="w-full max-w-2xl"
                    />
                </div>

                {/* Permissions Header */}
                <div className="grid grid-cols-11 gap-2 mb-4 px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg font-medium text-sm">
                    <div className="col-span-5 text-gray-700 dark:text-gray-300">
                        Modules
                    </div>
                    {['Permission', 'Active', 'Edit', 'Add', 'Draft'].map((label, index) => (
                        <div
                            key={label}
                            className={`col-span-1 text-center text-gray-700 dark:text-gray-300 ${index > 0 ? 'pl-2' : ''}`}
                        >
                            {label}
                        </div>
                    ))}
                    <div className="col-span-1"></div>
                </div>

                {/* Permissions List */}
                <div className="space-y-1">
                    {permissionStructure.map((section) => (
                        <div key={section.key} className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                            {section.type === 'single' ? (
                                // Single permission item
                                <div className="grid grid-cols-11 gap-2 items-center py-3 px-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded">
                                    <div className="col-span-5">
                                        <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                            {section.label}
                                        </span>
                                    </div>
                                    {PERM_TYPES.map((permType, index) => (
                                        <div
                                            key={permType}
                                            className={`col-span-1 flex justify-center ${index > 0 ? 'pl-2' : ''}`}
                                        >
                                            <Checkbox
                                                checked={getPermissionValue(section.key, permType)}
                                                onChange={(e) => updatePermission(section.key, permType, e.target.checked)}
                                            />
                                        </div>
                                    ))}
                                    <div className="col-span-1"></div>
                                </div>
                            ) : (
                                // Grouped permissions with expandable section
                                <>
                                    <div
                                        className="grid grid-cols-11 gap-2 items-center py-3 px-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded"
                                        onClick={() => toggleSection(section.key)}
                                    >
                                        <div className="col-span-5 flex items-center gap-2 min-w-0">
                                            <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                                {section.label}
                                            </span>
                                            {isModulePartiallyGranted(section) && (
                                                <span className={PERMISSION_CHIP.partial}>
                                                    Partial
                                                </span>
                                            )}
                                        </div>
                                        {PERM_TYPES.map((permType, index) => {
                                            const { checked, indeterminate } = getGroupColumnCheckState(section, permType);
                                            return (
                                            <div
                                                key={permType}
                                                className={`col-span-1 flex justify-center ${index > 0 ? 'pl-2' : ''}`}
                                            >
                                                <Checkbox
                                                    checked={checked}
                                                    indeterminate={indeterminate}
                                                    onChange={(e) => {
                                                        section.subItems.forEach(subItem => {
                                                            updatePermission(section.key, permType, e.target.checked, subItem.key);
                                                        });
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                        );})}
                                        <div className="col-span-1 flex justify-center">
                                            {expandedSections.has(section.key) ? (
                                                <ChevronUp className="w-4 h-4" />
                                            ) : (
                                                <ChevronDown className="w-4 h-4" />
                                            )}
                                        </div>
                                    </div>

                                    {expandedSections.has(section.key) && (
                                        <div className="bg-gray-50 dark:bg-gray-700/30">
                                            {section.subItems.map((subItem) => (
                                                <div
                                                    key={subItem.key}
                                                    className="grid grid-cols-11 gap-2 items-center py-2 px-4 border-t border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600/50"
                                                >
                                                    <div className="col-span-5 pl-6 flex items-center gap-2 min-w-0">
                                                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                                                            {subItem.label}
                                                        </span>
                                                        {isEssentialPermission(section.key, subItem.key, employeeEssentials) && (
                                                            <span className={PERMISSION_CHIP.essential}>
                                                                Essential
                                                            </span>
                                                        )}
                                                    </div>
                                                    {PERM_TYPES.map((permType, index) => (
                                                        <div
                                                            key={permType}
                                                            className={`col-span-1 flex justify-center ${index > 0 ? 'pl-2' : ''}`}
                                                        >
                                                            <Checkbox
                                                                checked={getPermissionValue(section.key, permType, subItem.key)}
                                                                onChange={(e) => updatePermission(section.key, permType, e.target.checked, subItem.key)}
                                                            />
                                                        </div>
                                                    ))}
                                                    <div className="col-span-1"></div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    ))}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
                    <Button
                        variant="outline"
                        onClick={handleBack}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="brand"
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        ) : (
                            <Save className="w-4 h-4 mr-2" />
                        )}
                        Update Permissions
                    </Button>
                </div>
            </div>
        </ContainerCard>
    );
};

export default UserPermissionEdit;